import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Check, Plus, Trash2, Wand2, Eye, EyeOff, Pencil, Copy, Tags, Users, Wallet,
  Search, Sparkles, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

import { useTranslation } from "react-i18next";
import { MerchantShell } from "@/components/merchant/merchant-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/merchant/client-plans")({
  head: () => ({
    meta: [
      { title: "Client Plans — Spayzo Merchant" },
      { name: "description", content: "Create and customize subscription plans to offer your clients." },
    ],
  }),
  component: ClientPlansPage,
});

type ClientPlan = {
  id: string;
  seedKey?: "starter" | "regular" | "vip";
  name: string;
  blurb: string;
  monthly: number;
  yearly: number;
  features: string[];
  published: boolean;
  recommended?: boolean;
  subscribers: number;
};

const SEED: ClientPlan[] = [
  {
    id: "starter", seedKey: "starter", name: "Starter", blurb: "Casual access for new visitors.",
    monthly: 9, yearly: 90, subscribers: 42, published: true,
    features: ["2 bookings / month", "Standard spaces", "Email support"],
  },
  {
    id: "regular", seedKey: "regular", name: "Regular", blurb: "Most popular for weekly users.",
    monthly: 29, yearly: 290, subscribers: 187, published: true, recommended: true,
    features: ["10 bookings / month", "All spaces", "10% off products", "Free cancellation"],
  },
  {
    id: "vip", seedKey: "vip", name: "VIP", blurb: "Unlimited perks for power users.",
    monthly: 79, yearly: 790, subscribers: 24, published: false,
    features: ["Unlimited bookings", "Priority seating", "20% off products", "Guest passes"],
  },
];

type FormState = {
  id?: string;
  name: string; blurb: string; monthly: string; yearly: string;
  features: string; recommended: boolean; published: boolean;
};

const EMPTY: FormState = {
  name: "", blurb: "", monthly: "", yearly: "",
  features: "", recommended: false, published: true,
};

function ClientPlansPage() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<ClientPlan[]>(SEED);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "published" | "draft">("all");
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const label = (p: ClientPlan) => ({
    name: p.seedKey ? t(`merchantClientPlans.seed.${p.seedKey}.name`) : p.name,
    blurb: p.seedKey ? t(`merchantClientPlans.seed.${p.seedKey}.blurb`) : p.blurb,
    features: p.seedKey
      ? p.features.map((_, i) => t(`merchantClientPlans.seed.${p.seedKey}.f${i + 1}`))
      : p.features,
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return plans.filter((p) => {
      if (filter === "published" && !p.published) return false;
      if (filter === "draft" && p.published) return false;
      if (!q) return true;
      const l = label(p);
      return (
        l.name.toLowerCase().includes(q) ||
        l.blurb.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, query, filter, t]);

  const subscribers = plans.reduce((s, p) => s + p.subscribers, 0);
  const mrr = plans.reduce((s, p) => s + p.subscribers * p.monthly, 0);
  const published = plans.filter((p) => p.published).length;
  const top = [...plans].sort((a, b) => b.subscribers - a.subscribers)[0];

  const openCreate = () => { setForm(EMPTY); setOpen(true); };
  const openEdit = (p: ClientPlan) => {
    const l = label(p);
    setForm({
      id: p.id, name: l.name, blurb: l.blurb,
      monthly: String(p.monthly), yearly: String(p.yearly),
      features: l.features.join("\n"),
      recommended: !!p.recommended, published: p.published,
    });
    setOpen(true);
  };

  const save = () => {
    if (!form.name.trim() || !form.monthly) {
      toast.error(t("merchantClientPlans.toast.required"));
      return;
    }
    const base = {
      seedKey: undefined,
      name: form.name.trim(),
      blurb: form.blurb.trim() || t("merchantClientPlans.defaultBlurb"),
      monthly: Number(form.monthly),
      yearly: form.yearly ? Number(form.yearly) : Number(form.monthly) * 10,
      features: form.features.split("\n").map((f) => f.trim()).filter(Boolean),
      recommended: form.recommended,
      published: form.published,
    };
    if (form.recommended) {
      // only one recommended
      setPlans((p) => p.map((x) => ({ ...x, recommended: false })));
    }
    if (form.id) {
      setPlans((p) => p.map((x) => (x.id === form.id ? { ...x, ...base } : x)));
      toast.success(t("merchantClientPlans.toast.updated"));
    } else {
      setPlans((p) => [...p, { id: `plan-${Date.now()}`, subscribers: 0, ...base }]);
      toast.success(t("merchantClientPlans.toast.created"));
    }
    setOpen(false);
  };

  const confirmDelete = () => {
    if (!deleteId) return;
    setPlans((p) => p.filter((x) => x.id !== deleteId));
    toast.success(t("merchantClientPlans.toast.removed"));
    setDeleteId(null);
  };
  const duplicate = (p: ClientPlan) => {
    const l = label(p);
    setPlans((arr) => [...arr, {
      ...p, seedKey: undefined, id: `plan-${Date.now()}`,
      name: `${l.name} ${t("merchantClientPlans.copySuffix")}`,
      blurb: l.blurb, features: l.features,
      subscribers: 0, published: false, recommended: false,
    }]);
    toast.success(t("merchantClientPlans.toast.duplicated"));
  };
  const togglePublished = (id: string) => {
    setPlans((p) => p.map((x) => (x.id === id ? { ...x, published: !x.published } : x)));
    const next = plans.find((x) => x.id === id);
    toast.success(next?.published
      ? t("merchantClientPlans.toast.hidden")
      : t("merchantClientPlans.toast.published"));
  };

  const filterLabel = { all: t("merchantClientPlans.toolbar.all"), published: t("merchantClientPlans.toolbar.published"), draft: t("merchantClientPlans.toolbar.draft") } as const;

  return (
    <MerchantShell
      title={t("merchantClientPlans.title")}
      subtitle={t("merchantClientPlans.subtitle")}
      actions={
        <Button onClick={openCreate} className="h-10 rounded-xl shadow-soft">
          <Plus className="h-4 w-4" /> {t("merchantClientPlans.newPlan")}
        </Button>
      }
    >
      {/* KPI strip */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("merchantClientPlans.kpi.activePlans")}</p>
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary"><Tags className="h-4 w-4" /></span>
          </div>
          <p className="mt-3 font-display text-3xl font-bold" dir="ltr">
            {published}<span className="text-base text-muted-foreground">/{plans.length}</span>
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{t("merchantClientPlans.kpi.activePlansHint")}</p>
        </Card>
        <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("merchantClientPlans.kpi.subscribers")}</p>
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary"><Users className="h-4 w-4" /></span>
          </div>
          <p className="mt-3 font-display text-3xl font-bold">{subscribers.toLocaleString()}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t("merchantClientPlans.kpi.subscribersHint")}</p>
        </Card>
        <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("merchantClientPlans.kpi.mrr")}</p>
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary"><Wallet className="h-4 w-4" /></span>
          </div>
          <p className="mt-3 font-display text-3xl font-bold"><Riyal /> {mrr.toLocaleString()}<span className="text-base text-muted-foreground">{t("merchantClientPlans.kpi.perMo")}</span>
          </p>
          <p className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-600">
            <TrendingUp className="h-3 w-3" /> {t("merchantClientPlans.kpi.mrrHint")}
          </p>
        </Card>
        <Card className="rounded-2xl border-primary/30 bg-gradient-to-br from-primary/10 via-background to-background p-5 shadow-soft">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("merchantClientPlans.kpi.topPlan")}</p>
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-primary-foreground"><Sparkles className="h-4 w-4" /></span>
          </div>
          <p className="mt-3 font-display text-2xl font-bold truncate">{top ? label(top).name : "—"}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("merchantClientPlans.kpi.topPlanHint", { count: top?.subscribers ?? 0 })}
          </p>
        </Card>
      </div>

      {/* Toolbar */}
      <div className="mt-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight">{t("merchantClientPlans.toolbar.heading")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("merchantClientPlans.toolbar.sub")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("merchantClientPlans.toolbar.search")}
              className="h-10 w-56 rounded-xl ps-9 text-start"
            />
          </div>
          <div className="inline-flex rounded-xl bg-background p-1 shadow-soft ring-1 ring-border/60">
            {(["all", "published", "draft"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  "rounded-lg px-3 py-1.5 text-xs font-semibold transition",
                  filter === f
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {filterLabel[f]}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {filtered.map((p) => {
          const l = label(p);
          return (
          <Card
            key={p.id}
            className={cn(
              "relative flex flex-col rounded-2xl p-6 shadow-soft transition hover:shadow-md",
              p.recommended
                ? "border-primary/40 bg-gradient-to-b from-primary/5 to-background ring-2 ring-primary/30"
                : "border-border/60",
              !p.published && "opacity-75",
            )}
          >
            {p.recommended && (
              <Badge className="absolute -top-3 start-6 rounded-full bg-primary text-primary-foreground shadow-soft hover:bg-primary">
                {t("merchantClientPlans.card.recommended")}
              </Badge>
            )}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-display text-lg font-bold truncate">{l.name}</p>
                  {!p.published && (
                    <Badge variant="outline" className="rounded-full text-[10px]">{t("merchantClientPlans.card.draft")}</Badge>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground truncate">{l.blurb}</p>
              </div>
              <Switch
                checked={p.published}
                onCheckedChange={() => togglePublished(p.id)}
                aria-label={t("merchantClientPlans.card.publishAria")}
              />
            </div>

            <div className="mt-5 flex items-baseline gap-1">
              <span className="font-display text-4xl font-bold tracking-tight"><Riyal /> {p.monthly}</span>
              <span className="text-sm text-muted-foreground">{t("merchantClientPlans.card.perMonth")}</span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("merchantClientPlans.card.orYearly")}{" "}
              <span className="font-semibold text-foreground"><Riyal /> {p.yearly}</span>
              {t("merchantClientPlans.card.perYear")}
              {p.monthly > 0 && (
                <span className="ms-1 text-emerald-600">
                  {t("merchantClientPlans.card.save", { percent: Math.max(0, Math.round((1 - p.yearly / (p.monthly * 12)) * 100)) })}
                </span>
              )}
            </p>

            <ul className="mt-5 space-y-2.5">
              {l.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <span className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                    <Check className="h-2.5 w-2.5" />
                  </span>
                  <span>{f}</span>
                </li>
              ))}
              {l.features.length === 0 && (
                <li className="text-xs text-muted-foreground">{t("merchantClientPlans.card.noFeatures")}</li>
              )}
            </ul>

            <div className="mt-6 flex items-center justify-between border-t border-border/60 pt-4 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" /> {t("merchantClientPlans.card.subscribers", { count: p.subscribers })}
              </span>
              <span className="inline-flex items-center gap-1.5">
                {p.published ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                {p.published ? t("merchantClientPlans.card.live") : t("merchantClientPlans.card.hidden")}
              </span>
            </div>

            <div className="mt-4 flex gap-2">
              <Button variant="outline" className="h-10 flex-1 rounded-xl" onClick={() => openEdit(p)}>
                <Pencil className="h-4 w-4" /> {t("merchantClientPlans.card.edit")}
              </Button>
              <Button variant="ghost" size="icon" className="h-10 w-10 rounded-xl" onClick={() => duplicate(p)} aria-label={t("merchantClientPlans.card.duplicate")}>
                <Copy className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-10 w-10 rounded-xl text-destructive hover:text-destructive" onClick={() => setDeleteId(p.id)} aria-label={t("merchantClientPlans.card.delete")}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        );})}

        {/* New plan tile */}
        <button
          onClick={openCreate}
          className="group flex min-h-[300px] flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border/70 bg-background/40 p-6 text-muted-foreground transition hover:border-primary/50 hover:bg-primary/5 hover:text-primary"
        >
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-primary/10 text-primary transition group-hover:scale-105 group-hover:bg-primary group-hover:text-primary-foreground">
            <Wand2 className="h-5 w-5" />
          </span>
          <p className="font-display text-base font-semibold">{t("merchantClientPlans.newTile.title")}</p>
          <p className="text-xs">{t("merchantClientPlans.newTile.sub")}</p>
        </button>

        {filtered.length === 0 && (
          <div className="col-span-full rounded-2xl border border-dashed border-border/70 p-10 text-center text-sm text-muted-foreground">
            {t("merchantClientPlans.empty")}
          </div>
        )}
      </div>

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-start">
              {form.id ? t("merchantClientPlans.dialog.editTitle") : t("merchantClientPlans.dialog.createTitle")}
            </DialogTitle>
            <DialogDescription className="text-start">{t("merchantClientPlans.dialog.description")}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="cp-name">{t("merchantClientPlans.dialog.name")}</Label>
              <Input id="cp-name" className="text-start" placeholder={t("merchantClientPlans.dialog.namePh")} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cp-blurb">{t("merchantClientPlans.dialog.blurb")}</Label>
              <Input id="cp-blurb" className="text-start" placeholder={t("merchantClientPlans.dialog.blurbPh")} value={form.blurb}
                onChange={(e) => setForm({ ...form, blurb: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="cp-monthly">{t("merchantClientPlans.dialog.monthly")}</Label>
                <Input id="cp-monthly" type="number" min="0" dir="ltr" className="text-start" placeholder="29" value={form.monthly}
                  onChange={(e) => setForm({ ...form, monthly: e.target.value })} />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="cp-yearly">{t("merchantClientPlans.dialog.yearly")}</Label>
                <Input id="cp-yearly" type="number" min="0" dir="ltr" className="text-start" placeholder="290" value={form.yearly}
                  onChange={(e) => setForm({ ...form, yearly: e.target.value })} />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cp-features">{t("merchantClientPlans.dialog.features")}</Label>
              <Textarea id="cp-features" rows={5} className="text-start" placeholder={t("merchantClientPlans.dialog.featuresPh")}
                value={form.features}
                onChange={(e) => setForm({ ...form, features: e.target.value })} />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 bg-surface p-3">
              <div>
                <Label htmlFor="cp-rec" className="text-sm">{t("merchantClientPlans.dialog.recommended")}</Label>
                <p className="text-xs text-muted-foreground">{t("merchantClientPlans.dialog.recommendedHint")}</p>
              </div>
              <Switch id="cp-rec" checked={form.recommended}
                onCheckedChange={(v) => setForm({ ...form, recommended: v })} />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 bg-surface p-3">
              <div>
                <Label htmlFor="cp-pub" className="text-sm">{t("merchantClientPlans.dialog.publish")}</Label>
                <p className="text-xs text-muted-foreground">{t("merchantClientPlans.dialog.publishHint")}</p>
              </div>
              <Switch id="cp-pub" checked={form.published}
                onCheckedChange={(v) => setForm({ ...form, published: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">{t("merchantClientPlans.dialog.cancel")}</Button>
            <Button onClick={save} className="rounded-xl">
              {form.id ? t("merchantClientPlans.dialog.save") : t("merchantClientPlans.dialog.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-start">{t("merchantClientPlans.confirm.title")}</AlertDialogTitle>
            <AlertDialogDescription className="text-start">
              {t("merchantClientPlans.confirm.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t("merchantClientPlans.confirm.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("merchantClientPlans.confirm.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MerchantShell>
  );
}
